import React, { FormEvent } from 'react'
import { useForm, Link } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

export default function New() {
  const { data, setData, post, processing, errors } = useForm({
    tool: {
      name: '',
      description: '',
      tool_type: 'web_service' as string,
      api_docs_url: '',
      guide_url: '',
      config: '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/manage/tools')
  }

  const isMcp = data.tool.tool_type === 'mcp'
  const isWebService = data.tool.tool_type === 'web_service'

  return (
    <SidebarLayout section="manage" title="New tool">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 800,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.tool.name}
            onChange={e => setData('tool', { ...data.tool, name: e.target.value })}
            error={'tool.name' in errors}
            helperText={(errors as Record<string, string>)['tool.name'] || 'e.g. gitlab-api, postgres-mcp'}
            sx={{ mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={data.tool.tool_type}
              label="Type"
              onChange={e => setData('tool', { ...data.tool, tool_type: e.target.value })}
              error={'tool.tool_type' in errors}
            >
              <MenuItem value="web_service">Web Service</MenuItem>
              <MenuItem value="mcp">MCP</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={data.tool.description}
            onChange={e => setData('tool', { ...data.tool, description: e.target.value })}
            error={'tool.description' in errors}
            helperText={(errors as Record<string, string>)['tool.description'] || 'What this tool does and when to use it'}
            sx={{ mb: 2.5 }}
          />
          {isWebService && (
            <>
              <TextField
                label="API Docs URL"
                fullWidth
                value={data.tool.api_docs_url}
                onChange={e => setData('tool', { ...data.tool, api_docs_url: e.target.value })}
                error={'tool.api_docs_url' in errors}
                helperText={(errors as Record<string, string>)['tool.api_docs_url'] || 'URL to API documentation'}
                sx={{ mb: 2.5 }}
              />
              <TextField
                label="Guide URL"
                fullWidth
                value={data.tool.guide_url}
                onChange={e => setData('tool', { ...data.tool, guide_url: e.target.value })}
                error={'tool.guide_url' in errors}
                helperText={(errors as Record<string, string>)['tool.guide_url'] || 'URL to guide (content will be auto-fetched)'}
                sx={{ mb: 2.5 }}
              />
            </>
          )}
          {isMcp && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                MCP server configuration (JSON)
              </Typography>
              <TextField
                label="Config"
                fullWidth
                multiline
                minRows={6}
                value={data.tool.config}
                onChange={e => setData('tool', { ...data.tool, config: e.target.value })}
                error={'tool.config' in errors}
                helperText={(errors as Record<string, string>)['tool.config'] || 'e.g. {"command": "npx", "args": ["-y", "@example/mcp-server"], "env": {}}'}
                sx={{ mb: 2.5, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Create tool
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/tools" style={{ color: '#1a1a1a' }}>Back to tools</Link>
          </Box>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
